/**
 * InvoicedPaymentCapture · iter303ak-fix37 — Invoiced · T30 · Action 10
 * ────────────────────────────────────────────────────────────────────────
 * Per-child payment entry with auto-calc of remaining. One row per
 * accepted child × payment (multiple partial payments supported).
 *
 * Persists to parent.invoiced_payments = {
 *   [child_id]: {
 *     payments: [{ amount, reference, code, bank, date, id, added_at }],
 *     due_amount, paid_total, remaining, status: 'paid'|'partial'|'unpaid'
 *   }
 * }
 * The T50 route pick reads this map + auto-suggests Paid vs Overdue.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { CreditCard, Plus, Trash2, CheckCircle2, AlertCircle } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const fmt = (n) => Number(n || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const uid = () => `p_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export default function InvoicedPaymentCapture({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const parentId = capturedData?.appraisal_id;
  const parent = useMemo(() => (
    capturedData?.appraisal_record || capturedData?.appraisal || {}
  ), [capturedData]);

  const acceptedChildren = useMemo(() => {
    const decisions = parent.child_decisions || {};
    return Object.entries(decisions)
      .filter(([, d]) => d?.decision === 'accepted')
      .map(([child_id, d]) => ({ child_id, ...d }));
  }, [parent]);

  const depositPick = useMemo(() => (parent.per_child_deposit_pick || {}), [parent]);
  const extras = useMemo(() => (parent.work_execution?.extras || []), [parent]);
  const extrasTags = useMemo(() => (parent.extras_contractor_tags || {}), [parent]);

  // Load state — start with any pre-existing invoiced_payments.
  const [state, setState] = useState(value?.state || parent.invoiced_payments || {});
  const [rowsMeta, setRowsMeta] = useState({});  // per child: {due_amount}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const loadDues = useCallback(async () => {
    if (!acceptedChildren.length) { setLoading(false); return; }
    setLoading(true);
    const meta = {};
    for (const c of acceptedChildren) {
      let base = 0;
      try {
        const { data: overview } = await api.get(`/appraisals/${c.child_id}/contractor-overview`);
        for (const sec of (overview?.sections || [])) {
          for (const ln of (sec?.lines || [])) {
            if (ln?.excluded_from_report) continue;
            base += Number(ln?.client_line_total || (Number(ln?.materials_total || 0) + Number(ln?.contractor_share || 0)) || 0);
          }
        }
      } catch { /* silent */ }
      let tagged_extras = 0;
      for (const ex of extras) {
        const tag = extrasTags[ex.id] || {};
        if (String(tag.child_id || '') === String(c.child_id)) tagged_extras += Number(ex.cost || 0);
      }
      const full = base + tagged_extras;
      const dep = Number(depositPick[c.child_id]?.amount || 0);
      meta[c.child_id] = { due_amount: Number((full - dep).toFixed(2)), full, deposit: dep };
    }
    setRowsMeta(meta);
    setLoading(false);
  }, [acceptedChildren, extras, extrasTags, depositPick]);
  useEffect(() => { loadDues(); }, [loadDues]);

  // Auto-calc summary + broadcast to parent runner.
  const summary = useMemo(() => {
    const s = {};
    for (const c of acceptedChildren) {
      const bucket = state[c.child_id] || {};
      const payments = bucket.payments || [];
      const paid = payments.reduce((a, p) => a + Number(p.amount || 0), 0);
      const due = rowsMeta[c.child_id]?.due_amount || 0;
      const remaining = Number((due - paid).toFixed(2));
      let status = 'unpaid';
      // iter303ak-fix40 · Child with $0 owing counts as settled.
      if (due <= 0.01) status = 'paid';
      else if (paid > 0 && remaining <= 0.01) status = 'paid';
      else if (paid > 0) status = 'partial';
      s[c.child_id] = { due_amount: due, paid_total: Number(paid.toFixed(2)), remaining, status, payments };
    }
    return s;
  }, [acceptedChildren, state, rowsMeta]);

  useEffect(() => {
    if (onChange) onChange({ state: summary });
  }, [summary]);  // eslint-disable-line react-hooks/exhaustive-deps

  const addPayment = (child_id) => {
    setState(prev => {
      const bucket = prev[child_id] || { payments: [] };
      return {
        ...prev,
        [child_id]: {
          ...bucket,
          payments: [...(bucket.payments || []), {
            id: uid(),
            amount: '',
            reference: '',
            code: '',
            bank: '',
            date: new Date().toISOString().slice(0, 10),
            added_at: new Date().toISOString(),
          }],
        },
      };
    });
  };

  const patchPayment = (child_id, payment_id, patch) => {
    setState(prev => {
      const bucket = prev[child_id] || { payments: [] };
      return {
        ...prev,
        [child_id]: {
          ...bucket,
          payments: (bucket.payments || []).map(p => p.id === payment_id ? { ...p, ...patch } : p),
        },
      };
    });
  };

  const deletePayment = (child_id, payment_id) => {
    setState(prev => {
      const bucket = prev[child_id] || { payments: [] };
      return {
        ...prev,
        [child_id]: { ...bucket, payments: (bucket.payments || []).filter(p => p.id !== payment_id) },
      };
    });
  };

  const overall = useMemo(() => {
    let due = 0, paid = 0;
    for (const c of acceptedChildren) {
      due += summary[c.child_id]?.due_amount || 0;
      paid += summary[c.child_id]?.paid_total || 0;
    }
    return { due: Number(due.toFixed(2)), paid: Number(paid.toFixed(2)), remaining: Number((due - paid).toFixed(2)) };
  }, [acceptedChildren, summary]);

  const persistAndContinue = async () => {
    if (!parentId) return;
    setSaving(true); setErr('');
    try {
      await api.patch(`/appraisals/${parentId}`, { invoiced_payments: summary });
      // Same payments land in the real ledger (Invoices + Client Statement), invoice flips to Paid when settled.
      await api.post('/workflow/side-effects/record-payments', { appraisal_id: parentId, by_child: summary });
      onNext();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="wf-invoiced-capture">
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
        <div className="flex items-start gap-3">
          <CreditCard className="w-5 h-5 mt-0.5 shrink-0 text-indigo-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-indigo-900">Enter Payment Details · Per Child</div>
            <div className="text-sm mt-1 text-indigo-800">
              {params.prompt || 'Log every payment received per child — amount · reference · code · bank · date. Add multiple rows for partial payments. Remaining auto-calcs.'}
            </div>
          </div>
        </div>
      </div>

      {loading && <div className="p-4 text-sm text-slate-500 italic">Loading due amounts…</div>}

      {!loading && acceptedChildren.map(c => {
        const s = summary[c.child_id] || { due_amount: 0, paid_total: 0, remaining: 0, status: 'unpaid' };
        const payments = state[c.child_id]?.payments || [];
        const statusColour = s.status === 'paid' ? 'emerald' : s.status === 'partial' ? 'amber' : 'slate';
        return (
          <div key={c.child_id} className={`border rounded-lg overflow-hidden border-${statusColour}-300 bg-${statusColour}-50/30`}
            data-testid={`wf-invoiced-capture-child-${c.child_id}`}>
            <div className="px-4 py-2 bg-white border-b border-slate-200 flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-slate-800">
                {c.quote_type_ref}{c.variant_label ? ` · ${c.variant_label}` : ''}
              </span>
              {c.appraisal_no && (
                <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">
                  A{c.appraisal_no}
                </span>
              )}
              <span className={`text-[10px] font-semibold uppercase tracking-widest rounded-full px-2 py-0.5 border
                ${s.status === 'paid' ? 'bg-emerald-100 border-emerald-300 text-emerald-800' :
                  s.status === 'partial' ? 'bg-amber-100 border-amber-300 text-amber-800' :
                  'bg-slate-100 border-slate-300 text-slate-700'}`}>
                {s.status === 'paid' ? 'Fully paid' : s.status === 'partial' ? 'Partial' : 'Unpaid'}
              </span>
              <div className="ml-auto flex items-center gap-4 text-xs">
                <span className="text-slate-500">Due <b className="font-mono text-slate-800">${fmt(s.due_amount)}</b></span>
                <span className="text-slate-500">Paid <b className="font-mono text-emerald-700">${fmt(s.paid_total)}</b></span>
                <span className={`text-slate-500 ${s.remaining > 0.01 ? 'text-rose-700' : ''}`}>
                  Remaining <b className="font-mono">${fmt(s.remaining)}</b>
                </span>
              </div>
            </div>
            <div className="p-3 space-y-2">
              {payments.length === 0 && (
                <div className="text-xs text-slate-500 italic px-2 py-1">
                  No payments logged. Click <b>Add payment</b> to log one; leave empty to route to Overdue at T50.
                </div>
              )}
              {payments.map((p, idx) => (
                <div key={p.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end p-2 bg-white border border-slate-200 rounded"
                  data-testid={`wf-invoiced-payment-row-${c.child_id}-${p.id}`}>
                  <div className="md:col-span-2">
                    <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Amount</label>
                    <input type="number" step="0.01" value={p.amount}
                      onChange={(e) => patchPayment(c.child_id, p.id, { amount: e.target.value })}
                      data-testid={`wf-invoiced-payment-amount-${c.child_id}-${p.id}`}
                      className="w-full px-2 py-1 text-xs border border-slate-300 rounded font-mono" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Reference</label>
                    <input type="text" value={p.reference}
                      onChange={(e) => patchPayment(c.child_id, p.id, { reference: e.target.value })}
                      placeholder="Invoice ref"
                      data-testid={`wf-invoiced-payment-ref-${c.child_id}-${p.id}`}
                      className="w-full px-2 py-1 text-xs border border-slate-300 rounded" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Code</label>
                    <input type="text" value={p.code}
                      onChange={(e) => patchPayment(c.child_id, p.id, { code: e.target.value })}
                      placeholder="Bank txn code"
                      data-testid={`wf-invoiced-payment-code-${c.child_id}-${p.id}`}
                      className="w-full px-2 py-1 text-xs border border-slate-300 rounded" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Bank</label>
                    <input type="text" value={p.bank}
                      onChange={(e) => patchPayment(c.child_id, p.id, { bank: e.target.value })}
                      placeholder="e.g. BNZ"
                      data-testid={`wf-invoiced-payment-bank-${c.child_id}-${p.id}`}
                      className="w-full px-2 py-1 text-xs border border-slate-300 rounded" />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Date</label>
                    <input type="date" value={p.date}
                      onChange={(e) => patchPayment(c.child_id, p.id, { date: e.target.value })}
                      data-testid={`wf-invoiced-payment-date-${c.child_id}-${p.id}`}
                      className="w-full px-2 py-1 text-xs border border-slate-300 rounded font-mono" />
                  </div>
                  <div className="md:col-span-1 flex items-end">
                    <button onClick={() => deletePayment(c.child_id, p.id)}
                      data-testid={`wf-invoiced-payment-delete-${c.child_id}-${p.id}`}
                      className="p-1.5 rounded text-rose-600 hover:bg-rose-50" title="Delete this payment row">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
              <button onClick={() => addPayment(c.child_id)}
                data-testid={`wf-invoiced-payment-add-${c.child_id}`}
                className="px-3 py-1 rounded text-xs font-semibold text-sky-700 bg-white border border-sky-300 hover:bg-sky-50 inline-flex items-center gap-1">
                <Plus size={12} /> Add payment
              </button>
            </div>
          </div>
        );
      })}

      {acceptedChildren.length > 0 && (
        <div className="p-3 rounded-lg bg-slate-800 text-white flex items-center gap-6"
          data-testid="wf-invoiced-capture-totals">
          <span className="text-[10px] uppercase tracking-widest opacity-70">Overall</span>
          <span><span className="opacity-70 text-xs">Due </span><b className="font-mono">${fmt(overall.due)}</b></span>
          <span><span className="opacity-70 text-xs">Paid </span><b className="font-mono text-emerald-300">${fmt(overall.paid)}</b></span>
          <span><span className="opacity-70 text-xs">Remaining </span>
            <b className={`font-mono ${overall.remaining > 0.01 ? 'text-rose-300' : 'text-emerald-300'}`}>
              ${fmt(overall.remaining)}
            </b>
          </span>
          <span className="ml-auto text-xs opacity-70">
            T50 will auto-suggest: <b>{overall.remaining <= 0.01 && overall.paid > 0 ? 'Paid' : 'Overdue'}</b>
          </span>
        </div>
      )}

      {err && <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">
        <AlertCircle className="w-4 h-4 mt-0.5" /> <span>{err}</span>
      </div>}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">← Back</button> : <span />}
        <div className="flex gap-2 items-center">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>}
          <button onClick={persistAndContinue} disabled={saving}
            data-testid="wf-invoiced-capture-continue"
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> {saving ? 'Saving…' : 'Save & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
